import type {
  AddGroupMemberRequest,
  CreateGroupRequest,
  GroupDetailResponse,
  GroupStatus,
  GroupSummaryResponse,
  PaginatedResponse,
  UpdateGroupRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListGroupsParams {
  readonly search?: string | undefined;
  readonly status?: GroupStatus | undefined;
  readonly parentGroupId?: string | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly limit?: number | undefined;
}

export const groupsApi = {
  list(params: ListGroupsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<GroupSummaryResponse>>('/groups', {
      query: { ...params },
      signal,
    });
  },

  get(id: string, signal?: AbortSignal) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}`, { signal });
  },

  create(body: CreateGroupRequest) {
    return apiRequest<GroupDetailResponse>('/groups', { method: 'POST', body });
  },

  update(id: string, body: UpdateGroupRequest) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}`, { method: 'PATCH', body });
  },

  /** There is no delete. Attendance references a group long after it stops meeting. */
  archive(id: string, reason: string) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restore(id: string) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}/restore`, { method: 'POST' });
  },

  // --- Roster ---------------------------------------------------------------

  addMember(id: string, body: AddGroupMemberRequest) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}/members`, { method: 'POST', body });
  },

  changeMemberRole(id: string, personId: string, role: AddGroupMemberRequest['role']) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}/members/${personId}`, {
      method: 'PATCH',
      body: { role },
    });
  },

  /**
   * The membership is ended, not deleted — it keeps its dates so a return visit
   * has a history. DELETE is the honest verb for the caller's intent even
   * though the storage is a soft end.
   */
  removeMember(id: string, personId: string, reason?: string | null) {
    return apiRequest<GroupDetailResponse>(`/groups/${id}/members/${personId}`, {
      method: 'DELETE',
      body: { reason: reason ?? null },
    });
  },
};
